import { data, useLoaderData, useNavigate } from 'react-router'
import { BusinessPagination } from '#app/components/business-pagination.tsx'
import { FilterCombobox } from '#app/components/filter-combobox.tsx'
import { RentalCard } from '#app/components/rental-card.tsx'
import { RentalSearch } from '#app/components/rental-search.tsx'
import { Icon } from '#app/components/ui/icon.js'
import { type SeoProps } from '#app/seo/seo.tsx'
import { buildItemListLd } from '#app/seo/structured-data.ts'
import { prisma } from '#app/utils/db.server.ts'
import { type Route } from './+types/index.ts'

const PAGE_SIZE = 36

export async function loader({ request }: Route.LoaderArgs) {
	const url = new URL(request.url)
	const pageParam = Number(url.searchParams.get('page') || '1')
	const q = url.searchParams.get('q')?.trim() || ''
	const rawPage =
		Number.isFinite(pageParam) && pageParam > 0 ? Math.floor(pageParam) : 1

	// Fetch cities and categories that have published rentals
	const [cities, categories] = await Promise.all([
		prisma.city.findMany({
			where: {
				rentalListings: { some: { status: 'published' } },
			},
			select: {
				id: true,
				name: true,
				slug: true,
			},
			orderBy: {
				name: 'asc',
			},
		}),
		prisma.category.findMany({
			where: {
				rentalListings: { some: { status: 'published' } },
			},
			select: {
				id: true,
				name: true,
				slug: true,
			},
			orderBy: {
				name: 'asc',
			},
		}),
	])

	const baseWhere = { status: 'published' as const }
	const where =
		q.length > 0
			? {
					AND: [
						baseWhere,
						{
							OR: [
								{ title: { contains: q, mode: 'insensitive' as const } },
								{ description: { contains: q, mode: 'insensitive' as const } },
							],
						},
					],
				}
			: baseWhere

	const total = await prisma.rentalListing.count({ where })

	const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
	const page = Math.min(rawPage, totalPages)
	const skip = (page - 1) * PAGE_SIZE

	const rentals = await prisma.rentalListing.findMany({
		where,
		select: {
			id: true,
			title: true,
			slug: true,
			description: true,
			hourlyRateCents: true,
			dailyRateCents: true,
			weeklyRateCents: true,
			monthlyRateCents: true,
			featuredMedia: {
				select: {
					url: true,
					altText: true,
				},
			},
			categories: {
				select: {
					id: true,
					name: true,
					slug: true,
				},
			},
			subcategories: {
				select: {
					id: true,
					name: true,
					slug: true,
				},
			},
			cities: {
				select: {
					id: true,
					name: true,
					slug: true,
				},
			},
			ownerBusiness: {
				select: {
					id: true,
					name: true,
					slug: true,
				},
			},
			ownerUser: {
				select: {
					id: true,
					name: true,
				},
			},
		},
		orderBy: [{ createdAt: 'desc' }, { title: 'asc' }],
		skip,
		take: PAGE_SIZE,
	})

	return data({
		rentals,
		cities,
		categories,
		page,
		pageSize: PAGE_SIZE,
		total,
	})
}

// Breadcrumb handle for automatic breadcrumb generation
export const handle = {
	breadcrumb() {
		return { name: 'Rentals', url: '/rentals' }
	},
	seo({
		loaderData,
		origin,
	}: {
		loaderData: Awaited<ReturnType<typeof loader>>['data']
		origin: string
	}): SeoProps | null {
		const canonical = `${origin}/rentals`

		const structuredData =
			loaderData.rentals.length > 0
				? [
						buildItemListLd({
							name: 'Rental Listings in Dallas-Fort Worth',
							items: loaderData.rentals.map((r) => ({
								name: r.title,
								url: `${origin}/rentals/${r.slug}`,
							})),
						}),
					]
				: undefined

		return {
			title: 'Rental Listings in Dallas-Fort Worth - RentDFW.com',
			description:
				'Browse rental listings for equipment, tools, and more in the Dallas-Fort Worth area. Find what you need to rent today.',
			canonical,
			openGraph: {
				title: 'Rental Listings in Dallas-Fort Worth - RentDFW.com',
				description:
					'Browse rental listings for equipment, tools, and more in the Dallas-Fort Worth area.',
				type: 'website',
				url: canonical,
			},
			twitter: {
				card: 'summary_large_image',
				title: 'Rental Listings in Dallas-Fort Worth - RentDFW.com',
				description:
					'Browse rental listings for equipment, tools, and more in the Dallas-Fort Worth area.',
			},
			structuredData,
		}
	},
}

export default function RentalsIndexPage() {
	const { rentals, cities, categories, page, pageSize, total } =
		useLoaderData<typeof loader>()
	const navigate = useNavigate()

	const totalPages = Math.max(1, Math.ceil(total / pageSize))

	function handleCityChange(value: string) {
		void navigate(`/rentals/city/${value}`)
	}

	function handleCategoryChange(value: string) {
		void navigate(`/rentals/category/${value}`)
	}

	return (
		<>
			{/* SEO is handled by SeoHead via handle.seo */}
			<div className="container mx-auto px-4 py-16">
				<div className="mb-8">
					<div className="mb-4 flex flex-col gap-4">
						<div className="mx-auto max-w-3xl text-center">
							<h1 className="text-4xl font-semibold text-balance">
								Rental Listings
							</h1>
							<p className="text-muted-foreground mt-2 mb-8 text-xl">
								Find top-rated rentals across the Dallas-Fort Worth metroplex.
								Connect directly with local businesses for equipment, tools,
								party supplies, and more.
							</p>
						</div>

						<div className="flex flex-col gap-3 md:flex-row">
							<div className="w-full">
								<RentalSearch total={total} />
							</div>
							{cities.length > 0 && (
								<div className="w-full md:w-64">
									<FilterCombobox
										options={cities.map((city) => ({
											label: city.name,
											value: city.slug,
										}))}
										onValueChange={handleCityChange}
										placeholder="Filter by city"
										searchPlaceholder="Search cities..."
										emptyText="No cities found"
									/>
								</div>
							)}
							{categories.length > 0 && (
								<div className="w-full md:w-64">
									<FilterCombobox
										options={categories.map((category) => ({
											label: category.name,
											value: category.slug,
										}))}
										onValueChange={handleCategoryChange}
										placeholder="Filter by category"
										searchPlaceholder="Search categories..."
										emptyText="No categories found"
									/>
								</div>
							)}
						</div>
					</div>
				</div>

				{total === 0 ? (
					<div className="bg-muted rounded-lg py-16 text-center">
						<Icon
							name="file-text"
							className="text-muted-foreground mx-auto mb-4 size-12"
						/>
						<h3 className="mb-2 text-xl font-semibold">No Rentals Available</h3>
						<p className="text-muted-foreground">
							No rental listings available at the moment. Check back soon!
						</p>
					</div>
				) : (
					<>
						<div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
							{rentals.map((rental) => (
								<RentalCard key={rental.id} rental={rental} />
							))}
						</div>

						<BusinessPagination currentPage={page} totalPages={totalPages} />
					</>
				)}
			</div>
		</>
	)
}
